import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DATE_FORMATS } from 'constants/dateFormats.js';
import { SUBMITTED_STUDENT_PORTFOLIO_ASSIGNMENT_STATUS_FILTER_OPTION } from 'constants/studentPortfolio.js';
import dayjs from 'dayjs';
import { head, isEmpty, isNull } from 'lodash';
import { useCurrentSchoolYear } from 'sdk';
import { noopPromise } from 'utils';

import { ViewToggle } from 'pages/Teachers/StudentPortfolio/components';
import { useNavbar } from 'pages/Teachers/shared/TeacherPageLayout';
import { formatDate, parseShortDateString } from 'utils/date.js';
import { useFetch } from 'utils/sdk';
import { parseParams, stringifyParams } from 'utils/urls';

import { fetchStudentPortfolio } from './sdk.js';

const parseDateFilters = (filters) => ({
  startDate: parseShortDateString({ date: filters.startDate }),
  endDate: parseShortDateString({ date: filters.endDate })
});

const validateFilterDates = (filters) => {
  const { startDate, endDate } = parseDateFilters(filters);
  if (!startDate || !endDate) return false;

  return (
    startDate.isValid() &&
    endDate.isValid() &&
    String(filters.startDate).indexOf('_') === -1 &&
    String(filters.endDate).indexOf('_') === -1 &&
    (startDate.isBefore(endDate) || startDate.isSame(endDate))
  );
};

export const validateFilters = (filters) => {
  return validateFilterDates(filters);
};

const prepareDateFilters = ({ startDate, endDate }) => ({
  start_date: formatDate({
    date: startDate,
    format: 'YYYY-MM-DD',
    fallback: ''
  }),
  end_date: formatDate({ date: endDate, format: 'YYYY-MM-DD', fallback: '' })
});

export const prepareAssignmentFilters = (filters) => ({
  assignment: filters.assignment,
  sections: filters.sections,
  status: filters.status,
  standards: filters.standards,
  ...prepareDateFilters(parseDateFilters(filters))
});

const limit = 20;
export const useInfiniteScroll = (hook, params) => {
  const [page, setPage] = useState(0);
  const [resultsCount, setResultsCount] = useState();
  // add end of loading when received last page

  const requestData = useMemo(
    () => ({ ...params, offset: page * limit, limit }),
    [params, page]
  );

  const triggerFetch = useMemo(() => !isNull(params), [params]);
  const result = hook(requestData, triggerFetch);

  const [aggregatedResults, setAggregatedResults] = useState({
    data: { results: [] },
    loading: true
  });

  useEffect(() => {
    if (!result.loading) {
      setAggregatedResults((x) => {
        const obj = {
          ...x,
          ...result,
          data: {
            ...result.data,
            results: [...x.data.results, ...result.data.results]
          }
        };
        return obj;
      });
    }
    if (result.loading) {
      setAggregatedResults((x) => ({ ...x, loading: true }));
    }
  }, [result]);

  useEffect(() => {
    if (!resultsCount) {
      setResultsCount(result.data.count);
    }
  }, [result, resultsCount]);

  const loadNextPage = useCallback(() => {
    if (resultsCount > page * limit) {
      setPage((x) => x + 1);
    }
  }, [resultsCount, page]);

  const resetPages = useCallback(({ withLoading = false }) => {
    setPage(0);
    setResultsCount(0);
    setAggregatedResults({ loading: withLoading, data: { results: [] } });
  }, []);

  return [aggregatedResults, loadNextPage, resetPages];
};

export const useStudentAssignments = (requestData, triggerFetch) => {
  const mapResponse = useCallback(
    (response) => ({
      ...response,
      results: response.results.map((assignment) => {
        const firstTask = head(assignment.tasks) || {};
        firstTask.isActive = true;

        const firstWork = head(firstTask.work) || {};
        firstWork.isActive = true;

        const dueAt = assignment.due_at ? dayjs(assignment.due_at) : dayjs();
        assignment.due_at = dueAt;

        return assignment;
      })
    }),
    []
  );

  const sdk = useMemo(
    () => (triggerFetch ? fetchStudentPortfolio : noopPromise),
    [triggerFetch]
  );

  const studentAssignments = useFetch(
    sdk,
    { data: { results: [] }, loading: true },
    requestData,
    mapResponse
  );

  const data = useMemo(() => {
    // If the page is not ready to perform the request we need to make sure that it's still in a loading state
    if (!triggerFetch) {
      return { ...studentAssignments, loading: true };
    }
    return studentAssignments;
  }, [studentAssignments, triggerFetch]);

  return data;
};

/**
 * `filtersOrCallback` is either the filters object or a callback that needs the filters
 * to be calculated. Same as the callback that `setA` uses in:
 *
 * const [a, setA] = useState();
 *
 */
export const getFiltersObject = (filtersOrCallback, filters) => {
  if (typeof filtersOrCallback === 'function') {
    return filtersOrCallback(filters);
  }
  return filtersOrCallback;
};

export const useFilters = (history) => {
  const [filters, setFilters] = useState({});

  useEffect(() => {
    if (!isEmpty(filters)) {
      history.push({
        search: stringifyParams(filters, { arrayFormat: 'bracket' })
      });
    }
  }, [filters, history]);

  return [filters, setFilters];
};

export const useNavigation = ({
  studentName,
  onTitleClick,
  viewMode,
  setViewMode
}) => {
  const navBar = useNavbar({
    title: studentName,
    onTitleClick,
    sticky: true,
    onBackButtonClick: onTitleClick
  });

  useEffect(() => {
    if (!viewMode || !setViewMode) return;
    navBar.navigation.setLeftSubheader(
      <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
    );
  }, [navBar.navigation, viewMode, setViewMode]);
};

const buildDefaultFilterValues = (currentYear, history) => {
  const initialFilters = {
    sections: [],
    standards: [],
    assignment: '',
    status: SUBMITTED_STUDENT_PORTFOLIO_ASSIGNMENT_STATUS_FILTER_OPTION.value,
    startDate: formatDate({
      date: currentYear.naive_start_date,
      format: DATE_FORMATS.SHORT
    }),
    endDate: formatDate({
      date: currentYear.naive_end_date,
      format: DATE_FORMATS.SHORT
    })
  };

  const queryParams = parseParams(history.location.search, {
    arrayFormat: 'bracket',
    parseNumbers: true
  });

  return { ...initialFilters, ...queryParams };
};

export const useDefaultFilters = (history) => {
  const { data: currentSchoolYear, isLoading: currentSchoolYearLoading } =
    useCurrentSchoolYear();

  const [defaultFilters, setDefaultFilters] = useState({});

  useEffect(() => {
    if (!currentSchoolYearLoading && currentSchoolYear) {
      const defaultFilterValues = buildDefaultFilterValues(
        currentSchoolYear,
        history
      );

      setDefaultFilters(defaultFilterValues);
    }
  }, [currentSchoolYear, currentSchoolYearLoading, history]);

  return defaultFilters;
};

export const useInitialFilters = () => {
  const { data: currentSchoolYear, isLoading: currentSchoolYearLoading } =
    useCurrentSchoolYear();

  const [initialFilters, setInitialFilters] = useState({});

  useEffect(() => {
    if (!currentSchoolYearLoading && currentSchoolYear) {
      setInitialFilters({
        sections: [],
        standards: [],
        assignment: '',
        status:
          SUBMITTED_STUDENT_PORTFOLIO_ASSIGNMENT_STATUS_FILTER_OPTION.value,
        startDate: formatDate({
          date: currentSchoolYear.naive_start_date,
          format: DATE_FORMATS.SHORT
        }),
        endDate: formatDate({
          date: currentSchoolYear.naive_end_date,
          format: DATE_FORMATS.SHORT
        })
      });
    }
  }, [currentSchoolYear, currentSchoolYearLoading]);

  return initialFilters;
};
